import { authenticatedBitswanBackendInstance } from '@/lib/api-client';
import type {
  AdvisorChangeReason,
  ChatConversation,
  ChatMessage,
  ConversationList,
  MyReport,
  ReportMessage,
  ReportThreadPayload,
} from '@/types/chat';

export async function fetchConversations() {
  const api = await authenticatedBitswanBackendInstance();
  const res = await api.get('/advisor-chat/conversations/');
  return res.data as ConversationList;
}

export async function fetchMessages(conversationId: string) {
  const api = await authenticatedBitswanBackendInstance();
  const res = await api.get(
    `/advisor-chat/conversations/${conversationId}/messages/`,
  );
  return res.data as { conversation: ChatConversation; messages: ChatMessage[] };
}

export async function sendMessage(
  conversationId: string,
  body: string,
  files: File[],
) {
  const api = await authenticatedBitswanBackendInstance();
  const form = new FormData();
  form.append('body', body);
  files.forEach((file) => form.append('attachments', file));

  const res = await api.post(
    `/advisor-chat/conversations/${conversationId}/messages/`,
    form,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return res.data as ChatMessage;
}

export async function markRead(conversationId: string) {
  const api = await authenticatedBitswanBackendInstance();
  await api.post(`/advisor-chat/conversations/${conversationId}/read/`);
}

export async function fetchUnreadCount() {
  const api = await authenticatedBitswanBackendInstance();
  const res = await api.get('/advisor-chat/unread/');
  return res.data as { unread: number; roles: Array<'customer' | 'advisor'> };
}

export async function downloadAttachment(attachmentId: string, filename: string) {
  const api = await authenticatedBitswanBackendInstance();
  const res = await api.get(`/advisor-chat/attachments/${attachmentId}/`, {
    responseType: 'blob',
  });
  const url = URL.createObjectURL(res.data as Blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export async function changeAdvisor(input: {
  advisor_profile_id: string;
  reason: AdvisorChangeReason;
  comment: string;
}) {
  const api = await authenticatedBitswanBackendInstance();
  const res = await api.post('/advisor-chat/change-advisor/', input);
  return res.data as {
    advisor_profile_id: string;
    advisor_display_name: string;
    conversation_id: string;
  };
}


export type ReportCategory = { value: string; label: string };

export async function fetchReportOptions() {
  const api = await authenticatedBitswanBackendInstance();
  const res = await api.get('/advisor-chat/report-advisor/');
  return res.data as { categories: ReportCategory[]; reports: MyReport[] };
}

export async function reportAdvisor(input: {
  advisor_profile_id: string;
  category: string;
  details: string;
}) {
  const api = await authenticatedBitswanBackendInstance();
  const res = await api.post('/advisor-chat/report-advisor/', input);
  return res.data as { id: string; status: string; message: string };
}

export async function fetchReportThread(reportId: string) {
  const api = await authenticatedBitswanBackendInstance();
  const res = await api.get(`/advisor-reports/${reportId}/messages/`);
  return res.data as ReportThreadPayload;
}

export async function sendReportMessage(
  reportId: string,
  body: string,
  files: File[],
) {
  const api = await authenticatedBitswanBackendInstance();
  const form = new FormData();
  form.append('body', body);
  files.forEach((file) => form.append('attachments', file));

  const res = await api.post(`/advisor-reports/${reportId}/messages/`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return res.data as ReportMessage;
}
